import React, { useEffect, useState } from "react";
import { api, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, GoldButton, Card, EmptyState, ui } from "@/components/Primitives";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { BRANDING } from "@/config/branding";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, TrendingUp } from "lucide-react";

const TYPES = ["Stock", "ETF", "Crypto", "Property", "Bond", "Cash", "Other"];
const DONUT = BRANDING.chartPalette;
const blank = { name: "", asset_type: "Stock", units: "", buy_price: "", current_price: "" };

export default function Investments() {
  const { user } = useAuth();
  const cur = user?.currency || "AUD";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = () => api.get("/investments").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setOpen(true); };
  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    const payload = { ...form, units: Number(form.units || 0), buy_price: Number(form.buy_price || 0), current_price: Number(form.current_price || 0) };
    try {
      if (editing) await api.put(`/investments/${editing.id}`, payload);
      else await api.post("/investments", payload);
      toast.success(editing ? "Updated" : "Added"); setOpen(false); load();
    } catch { toast.error("Could not save"); }
  };
  const remove = async (id) => { await api.delete(`/investments/${id}`); toast.success("Removed"); load(); };

  const totalValue = items.reduce((s, i) => s + i.units * i.current_price, 0);
  const totalCost = items.reduce((s, i) => s + i.units * i.buy_price, 0);
  const gain = totalValue - totalCost;
  const allocation = items.map((i) => ({ name: i.name, value: i.units * i.current_price }));

  return (
    <div>
      <PageHeader title="Portfolio" subtitle="Your holdings, allocation and unrealised performance."
        action={<GoldButton data-testid="add-investment-btn" onClick={openNew}><Plus size={16} /> Add Holding</GoldButton>} />

      {items.length === 0 ? (
        <Card><EmptyState icon={TrendingUp} title="No holdings yet" hint="Add shares, ETFs, crypto or property to track your portfolio." /></Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
            <Card><div className="text-[11px] uppercase tracking-[0.16em] text-[#8A7F70]">Market Value</div>
              <div className="font-display text-3xl mt-3 font-light tabular-nums text-[#2C2A28]">{fmt(totalValue, cur)}</div></Card>
            <Card><div className="text-[11px] uppercase tracking-[0.16em] text-[#8A7F70]">Total Cost</div>
              <div className="font-display text-3xl mt-3 font-light tabular-nums text-[#2C2A28]">{fmt(totalCost, cur)}</div></Card>
            <Card><div className="text-[11px] uppercase tracking-[0.16em] text-[#8A7F70]">Unrealised P/L</div>
              <div className={`font-display text-3xl mt-3 font-light tabular-nums ${gain >= 0 ? "text-[#5a9a6a]" : "text-[#b06b6b]"}`}>{gain >= 0 ? "+" : ""}{fmt(gain, cur)}</div></Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <Card className="lg:col-span-2 p-0 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="investments-table">
                  <thead>
                    <tr className="text-xs uppercase tracking-[0.1em] text-[#A99F90] border-b border-[#EAE0D2]">
                      <th className="text-left py-4 px-6 font-normal">Asset</th>
                      <th className="text-right py-4 px-3 font-normal">Units</th>
                      <th className="text-right py-4 px-3 font-normal">Value</th>
                      <th className="text-right py-4 px-3 font-normal">P/L</th>
                      <th className="text-right py-4 px-6 font-normal"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => {
                      const value = it.units * it.current_price;
                      const pl = value - it.units * it.buy_price;
                      return (
                        <tr key={it.id} className="border-b border-[#F0E9DD] hover:bg-white/60" data-testid={`inv-row-${it.id}`}>
                          <td className="py-4 px-6 text-[#2C2A28]"><div>{it.name}</div><span className="text-xs text-[#A99F90]">{it.asset_type}</span></td>
                          <td className="py-4 px-3 text-right font-mono-data text-[#6b6256]">{it.units}</td>
                          <td className="py-4 px-3 text-right font-mono-data text-[#2C2A28]">{fmt(value, cur)}</td>
                          <td className={`py-4 px-3 text-right font-mono-data ${pl >= 0 ? "text-[#5a9a6a]" : "text-[#b06b6b]"}`}>{pl >= 0 ? "+" : ""}{fmt(pl, cur)}</td>
                          <td className="py-4 px-6 text-right">
                            <button data-testid={`edit-inv-${it.id}`} onClick={() => openEdit(it)} className="text-[#8A7F70] hover:text-[#C6A45C] p-1.5"><Pencil size={15} /></button>
                            <button data-testid={`delete-inv-${it.id}`} onClick={() => remove(it.id)} className="text-[#8A7F70] hover:text-[#C99FA0] p-1.5"><Trash2 size={15} /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card>
              <h3 className="font-display text-xl font-normal mb-2 text-[#2C2A28]">Allocation</h3>
              <ResponsiveContainer width="100%" height={210}>
                <PieChart>
                  <Pie data={allocation} dataKey="value" nameKey="name" innerRadius={50} outerRadius={86} paddingAngle={2} stroke="none">
                    {allocation.map((_, i) => <Cell key={i} fill={DONUT[i % DONUT.length]} />)}
                  </Pie>
                  <Tooltip content={({ active, payload }) => active && payload?.length ? (
                    <div className="bg-white/95 border border-[#EAE0D2] rounded-xl p-3 text-xs shadow-lg">
                      <div className="text-[#2C2A28]">{payload[0].name}</div>
                      <div className="gold-text font-mono-data">{fmt(payload[0].value, cur)}</div>
                    </div>) : null} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {allocation.map((a, i) => (
                  <div key={a.name} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2 text-[#6b6256]"><span className="w-2 h-2 rounded-full" style={{ background: DONUT[i % DONUT.length] }} />{a.name}</span>
                    <span className="font-mono-data text-[#8A7F70]">{totalValue > 0 ? ((a.value / totalValue) * 100).toFixed(0) : 0}%</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={ui.dialog}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-light">{editing ? "Edit Holding" : "Add Holding"}</DialogTitle>
            <DialogDescription className="text-[#8A7F70]">Track an asset in your portfolio.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={ui.label}>Name / Ticker</Label>
                <Input data-testid="inv-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={ui.input} placeholder="VAS / Apple" />
              </div>
              <div>
                <Label className={ui.label}>Type</Label>
                <Select value={form.asset_type} onValueChange={(v) => setForm({ ...form, asset_type: v })}>
                  <SelectTrigger data-testid="inv-type" className={ui.selectTrigger}><SelectValue /></SelectTrigger>
                  <SelectContent className={ui.selectContent}>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className={ui.label}>Units</Label><Input data-testid="inv-units" type="number" value={form.units} onChange={(e) => setForm({ ...form, units: e.target.value })} className={ui.input} placeholder="10" /></div>
              <div><Label className={ui.label}>Buy price</Label><Input data-testid="inv-buy" type="number" value={form.buy_price} onChange={(e) => setForm({ ...form, buy_price: e.target.value })} className={ui.input} placeholder="80" /></div>
              <div><Label className={ui.label}>Current</Label><Input data-testid="inv-current" type="number" value={form.current_price} onChange={(e) => setForm({ ...form, current_price: e.target.value })} className={ui.input} placeholder="92" /></div>
            </div>
          </div>
          <DialogFooter>
            <GoldButton data-testid="save-inv-btn" onClick={save} className="w-full">{editing ? "Save Changes" : "Add Holding"}</GoldButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
